import { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeft, ChevronRight, Users, Clock, Star, Bot, Lock } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import useGameRegistry from '../hooks/useGameRegistry';
import useCarouselDirection from '../hooks/useCarouselDirection';
import { useAuthStore } from '../hooks/useAuthStore';

const GameSelectionCarousel = ({ onGameSelect, selectedGame, onConfirm }) => {
  const { t } = useTranslation(['gameCreation', 'common']);
  const { games, loading, error } = useGameRegistry();
  const { user } = useAuthStore();

  const {
    currentIndex,
    direction,
    clickedButton,
    directionSwitched,
    isAnimating,
    next,
    prev,
    goTo,
    onAnimationStart,
    onAnimationComplete,
  } = useCarouselDirection(0, games.length);

  const [imageErrors, setImageErrors] = useState({});
  const [maxDescriptionHeight, setMaxDescriptionHeight] = useState(0);
  const carouselRef = useRef(null);
  const descriptionRef = useRef(null);

  // Reset to first game when games load
  useEffect(() => {
    if (games.length > 0 && !selectedGame) {
      goTo(0);
      onGameSelect?.(games[0]);
    }
  }, [games, selectedGame, onGameSelect]);

  // Update selected game when index changes
  useEffect(() => {
    if (games[currentIndex] && games[currentIndex] !== selectedGame) {
      onGameSelect?.(games[currentIndex]);
    }
  }, [currentIndex, games, selectedGame, onGameSelect]);

  // Measure description height to set fixed container height
  useEffect(() => {
    if (descriptionRef.current) {
      const height = descriptionRef.current.scrollHeight;
      if (height > maxDescriptionHeight) {
        setMaxDescriptionHeight(height);
      }
    }
  }, [currentIndex, games, maxDescriptionHeight]);

  const nextGame = next;
  const prevGame = prev;
  const goToGame = goTo;

  const handleImageError = (gameId) => {
    setImageErrors(prev => ({ ...prev, [gameId]: true }));
  };

  const getGameCategoryColor = (category) => {
    switch (category) {
      case 'strategy': return 'bg-blue-600';
      case 'puzzle': return 'bg-green-600';
      case 'action': return 'bg-red-600';
      case 'casual': return 'bg-yellow-600';
      case 'educational': return 'bg-purple-600';
      default: return 'bg-gray-600';
    }
  };

  if (loading) {
    return (
      <div className="space-y-6 animate-pulse">
        {/* Image skeleton */}
        <div className="glass-effect rounded-xl overflow-hidden">
          <div className="h-64 bg-gray-700/60" />
          <div className="p-6 min-h-80 space-y-4">
            <div className="h-6 bg-gray-700/60 rounded w-1/3" />
            <div className="space-y-2">
              <div className="h-4 bg-gray-700/60 rounded w-full" />
              <div className="h-4 bg-gray-700/60 rounded w-2/3" />
            </div>
            <div className="grid grid-cols-3 gap-4 pt-2">
              {[0, 1, 2].map(i => (
                <div key={i} className="space-y-2 flex flex-col items-center">
                  <div className="h-4 w-4 bg-gray-700/60 rounded-full" />
                  <div className="h-3 bg-gray-700/60 rounded w-10" />
                  <div className="h-4 bg-gray-700/60 rounded w-12" />
                </div>
              ))}
            </div>
            <div className="h-10 bg-gray-700/60 rounded-lg mt-4" />
          </div>
        </div>
      </div>
    );
  }

  if (error || games.length === 0) {
    return (
      <div className="text-center py-12">
        <div className="w-16 h-16 bg-gray-700 rounded-full flex items-center justify-center mx-auto mb-4">
          <Users className="w-8 h-8 text-gray-400" />
        </div>
        <h3 className="text-lg font-semibold text-white mb-2">
          {t('carousel.noGames', { ns: 'gameCreation' })}
        </h3>
        {error && (
          <p className="text-gray-400 text-sm">{error}</p>
        )}
      </div>
    );
  }

  const currentGame = games[currentIndex];
  const isCurrentGameLocked = Boolean(user?.isAnonymous) && currentGame?.tier === 'premium';

  const cardVariants = {
    initial: (d) => ({ opacity: 0, x: d * 300 }),
    animate: { opacity: 1, x: 0 },
    exit: (d) => ({ opacity: 0, x: d * -300 }),
  };

  return (
    <div className="space-y-6">
      {/* Main Carousel */}
      <div className="relative overflow-hidden" ref={carouselRef}>
        {/* Navigation Buttons */}
        {games.length > 1 && (
          <>
            <button
              onClick={prevGame}
              className="absolute left-2 top-32 z-10 w-10 h-10 bg-black/50 hover:bg-black/70 backdrop-blur-sm rounded-full flex items-center justify-center transition-colors"
              aria-label={t('carousel.navigation.previous', { ns: 'gameCreation' })}
            >
              <ChevronLeft className="w-5 h-5 text-white" />
            </button>

            <button
              onClick={nextGame}
              className="absolute right-2 top-32 z-10 w-10 h-10 bg-black/50 hover:bg-black/70 backdrop-blur-sm rounded-full flex items-center justify-center transition-colors"
              aria-label={t('carousel.navigation.next', { ns: 'gameCreation' })}
            >
              <ChevronRight className="w-5 h-5 text-white" />
            </button>
          </>
        )}

        {/* Game Card */}
        <AnimatePresence mode="wait" custom={direction}>
          <motion.div
            key={currentIndex}
            custom={direction}
            variants={cardVariants}
            initial="initial"
            animate="animate"
            exit="exit"
            transition={{ duration: 0.3 }}
            onAnimationStart={onAnimationStart}
            onAnimationComplete={onAnimationComplete}
            className="glass-effect rounded-xl overflow-hidden"
          >
            {/* Game Image — a fixed height, same for every game, with each
                image's OWN intrinsic aspect ratio (via object-contain)
                determining its width within that height. No per-game
                CSS needed: preview.jpg for Coming Attractions is now
                genuinely wide (2400×1336 board art, 1.796 aspect) instead
                of the old force-cropped 1:1 square that clipped its
                sides, so it naturally renders at the same height as every
                other (roughly square) game's preview, just wider. */}
            <div className="relative h-64 bg-gray-800 flex items-center justify-center overflow-hidden">
              {!imageErrors[currentGame?.name] ? (
                <img
                  src={currentGame?.image}
                  alt={currentGame?.displayName}
                  className="max-w-full max-h-full object-contain"
                  onError={() => handleImageError(currentGame?.name)}
                />
              ) : (
                <div className="w-full h-full bg-gradient-to-br from-gray-700 to-gray-800 flex items-center justify-center">
                  <div className="text-center">
                    <Users className="w-12 h-12 text-gray-400 mx-auto mb-2" />
                    <p className="text-gray-400 font-semibold">{currentGame?.displayName}</p>
                  </div>
                </div>
              )}

              {/* Game Category Badge */}
              <div className="absolute top-4 left-4">
                <span className={`px-3 py-1 rounded-full text-xs font-medium text-white ${getGameCategoryColor(currentGame?.category)}`}>
                  {currentGame?.category?.toUpperCase()}
                </span>
              </div>

              {/* Bot Support Indicator */}
              {currentGame?.supportsBots && (
                <div className="absolute top-4 right-4">
                  <div className="bg-green-600 px-2 py-1 rounded-full flex items-center gap-1">
                    <Bot className="w-3 h-3 text-white" />
                    <span className="text-xs font-medium text-white">BOT</span>
                  </div>
                </div>
              )}

              {/* Premium Badge — games restricted to signed-in users. Guests
                  (who can't select this game at all) get it centered over
                  the preview as a full-size, unmissable lock; signed-in
                  users just get a smaller informational badge in the
                  lower-left corner, out of the way of the category/bot
                  badges above. */}
              {currentGame?.tier === 'premium' && (
                isCurrentGameLocked ? (
                  <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                    <div className="bg-purple-600/90 shadow-lg px-5 py-2.5 rounded-full flex items-center gap-2">
                      <Lock className="w-5 h-5 text-white" />
                      <span className="text-base font-semibold text-white tracking-wide">
                        {t('premium.badge', { ns: 'gameCreation' })}
                      </span>
                    </div>
                  </div>
                ) : (
                  <div className="absolute bottom-4 left-4 pointer-events-none">
                    <div className="bg-purple-600/90 shadow-lg px-2.5 py-1 rounded-full flex items-center gap-1">
                      <Lock className="w-3 h-3 text-white" />
                      <span className="text-xs font-medium text-white tracking-wide">
                        {t('premium.badge', { ns: 'gameCreation' })}
                      </span>
                    </div>
                  </div>
                )
              )}
            </div>

            {/* Game Info */}
            <div className="p-6 min-h-80 overflow-hidden">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h3 className="text-xl font-bold text-white mb-2">
                    {currentGame?.displayName}
                  </h3>
                  <p 
                    ref={descriptionRef}
                    className="text-gray-400 text-sm line-clamp-2"
                    style={{ minHeight: maxDescriptionHeight || 'auto' }}
                  >
                    {currentGame?.description}
                  </p>
                </div>
              </div>

              {/* Game Stats */}
              <div className="grid grid-cols-3 gap-4 mb-4">
                <div className="text-center">
                  <div className="flex items-center justify-center gap-1 text-gray-400 mb-1">
                    <Users className="w-4 h-4" />
                  </div>
                  <p className="text-xs text-gray-500">
                    {t('players', { ns: 'common' })}
                  </p>
                  <p className="text-sm font-semibold text-white">
                    {currentGame?.minPlayers}-{currentGame?.maxPlayers}
                  </p>
                  {currentGame?.supportsBots && currentGame?.botConfig?.maxBots > 0 && (
                    <p className="text-xs text-green-400 flex items-center justify-center gap-1 mt-1">
                      <Bot className="w-3 h-3" />
                      up to {currentGame.botConfig.maxBots} bots
                    </p>
                  )}
                </div>

                <div className="text-center">
                  <div className="flex items-center justify-center gap-1 text-gray-400 mb-1">
                    <Clock className="w-4 h-4" />
                  </div>
                  <p className="text-xs text-gray-500">
                    {t('duration', { ns: 'common' })}
                  </p>
                  <p className="text-sm font-semibold text-white">
                    {currentGame?.estimatedDuration || '15-30m'}
                  </p>
                </div>

                <div className="text-center">
                  <div className="flex items-center justify-center gap-1 text-gray-400 mb-1">
                    <Star className="w-4 h-4" />
                  </div>
                  <p className="text-xs text-gray-500">
                    {t('difficulty', { ns: 'common' })}
                  </p>
                  <p className="text-sm font-semibold text-white capitalize">
                    {currentGame?.difficulty || 'Medium'}
                  </p>
                </div>
              </div>

              {/* Selection Button */}
              <motion.button
                whileHover={isCurrentGameLocked ? {} : { scale: 1.02 }}
                whileTap={isCurrentGameLocked ? {} : { scale: 0.98 }}
                onClick={() => {
                  if (isCurrentGameLocked) return;
                  onGameSelect?.(currentGame);
                  onConfirm?.();
                }}
                disabled={isCurrentGameLocked}
                title={isCurrentGameLocked ? t('premium.signInToPlay', { ns: 'gameCreation' }) : undefined}
                className={`w-full font-medium py-3 px-4 rounded-lg transition-colors flex items-center justify-center gap-2 ${
                  isCurrentGameLocked
                    ? 'bg-gray-700 text-gray-400 cursor-not-allowed'
                    : 'bg-blue-600 hover:bg-blue-700 text-white'
                }`}
              >
                {isCurrentGameLocked && <Lock className="w-4 h-4" />}
                {isCurrentGameLocked
                  ? t('premium.signInToPlay', { ns: 'gameCreation' })
                  : t('carousel.navigation.select', { ns: 'gameCreation' })}
              </motion.button>
            </div>
          </motion.div>
        </AnimatePresence>
      </div>

      {/* Dots Indicator */}
      {games.length > 1 && (
        <div className="flex justify-center gap-2">
          {games.map((_, index) => (
            <button
              key={index}
              onClick={() => goToGame(index)}
              className={`w-2 h-2 rounded-full transition-colors ${
                index === currentIndex
                  ? 'bg-blue-600'
                  : 'bg-gray-600 hover:bg-gray-500'
              }`}
              aria-label={`Go to game ${index + 1}`}
            />
          ))}
        </div>
      )}

      {/* Game Thumbnails */}
      {games.length > 1 && (
        <div className="flex gap-2 justify-center overflow-x-auto pb-2">
          {games.map((game, index) => (
            <motion.button
              key={game.name}
              onClick={() => goToGame(index)}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className={`
                relative flex-shrink-0 w-16 h-16 rounded-lg overflow-hidden border-2 transition-colors
                ${index === currentIndex
                  ? 'border-blue-500'
                  : 'border-gray-600 hover:border-gray-500'
                }
              `}
            >
              {game.tier === 'premium' && (
                <div className="absolute top-0.5 right-0.5 bg-purple-600 rounded-full p-0.5 z-10">
                  <Lock className="w-2.5 h-2.5 text-white" />
                </div>
              )}
              {!imageErrors[game.name] ? (
                <img
                  src={game.image}
                  alt={game.displayName}
                  className="max-w-full max-h-full object-contain"
                  onError={() => handleImageError(game.name)}
                />
              ) : (
                <div className="w-full h-full bg-gray-700 flex items-center justify-center">
                  <span className="text-xs font-bold text-gray-400">
                    {game.displayName.charAt(0)}
                  </span>
                </div>
              )}
            </motion.button>
          ))}
        </div>
      )}
    </div>
  );
};

export default GameSelectionCarousel;